import React from "react";
import { Box, Tooltip } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";

import { EsgRecIcon, ParameterBasedEstimateIcon, SitemultiRegion, UtilitySpecificEmissionsFactorsIcon } from "assets";

import { EMPTY_CELL_VALUE } from "consts";
import { capitalizeString, isNullOrUndefined } from "utils";
import { CollectorDTO } from "api";

export const SiteColumns: MRT_ColumnDef<any>[] = [
  {
    accessorKey: "tags",
    header: "Tags",
    size: 0.4,
    enableColumnActions: false,
    enableColumnFilter: false,
    Cell: ({ row }) => {
      const {
        collectors,
        estimateIndicator: showEstimateIndicator,
        emissionFactorIndicator,
        recsIndicator: showRecIndicator,
        multipleRegion,
        regionUpdated,
        region,
        country,
        spendBasedIndicator,
        customRegion,
      } = row.original;

      const collectorsSpendBasedIndicator = collectors?.some((collector: CollectorDTO) => collector.spendBased);
      const showSpendBasedIndicator = emissionFactorIndicator || collectorsSpendBasedIndicator || spendBasedIndicator;
      const showMultipleRegionIndicator = multipleRegion === true && regionUpdated === false;

      return (
        <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}>
          {((showMultipleRegionIndicator || region === null || country === null) && customRegion === null) && (
            <Tooltip
              title={multipleRegion ? "Multiple Region Codes Found"
                : region === null ? "Missing Region Code"
                  : country === null ? "Missing Country Code"
                    : ""
              }
              placement="top"
            >
              <SitemultiRegion
                style={{
                  width: "20px",
                  height: "20px",
                  marginTop: "1px",
                }}
              />
            </Tooltip>
          )}
          <Tooltip
            title={
              showSpendBasedIndicator
                ? "There are Spend Based Estimations at this site"
                : "There are no Spend Based Estimations at this site"
            }
            placement="top"
          >
            <Box sx={{ mt: '4px' }}>
              <UtilitySpecificEmissionsFactorsIcon color={showSpendBasedIndicator ? "#388010" : "#CCCCCC"} />
            </Box>
          </Tooltip>
          <Tooltip
            placement="top"
            title={
              showEstimateIndicator
                ? "There are Parameter Based Estimations at this site"
                : "There are no Parameter Based Estimations at this site"
            }
          >
            <ParameterBasedEstimateIcon color={showEstimateIndicator ? "#388010" : "#CCCCCC"} />
          </Tooltip>
          <Tooltip
            title={
              showRecIndicator
                ? "This site has a Utility Provider Renewable Energy Rate applied to its emissions"
                : "This site has not a Utility Provider Renewable Energy Rate applied to its emissions"
            }
            placement="top"
          >
            <EsgRecIcon color={showRecIndicator ? "#388010" : "#CCCCCC"} />
          </Tooltip>
        </Box>
      );
    },
  },
  {
    accessorKey: "customerSiteId",
    header: "Customer Site ID",
    enableColumnFilter: true,
    Cell: ({ cell }) => {
      const value = cell.getValue();
      if (isNullOrUndefined(value)) {
        return EMPTY_CELL_VALUE;
      }
      return (
        <div>{value as string}</div>
      )
    },
    size: 0.4,
  },
  {
    accessorKey: "name",
    header: "Site Name",
    enableColumnFilter: true,
    size: 0.4,
  },
  {
    accessorKey: "type",
    header: "Type of Site",
    enableColumnFilter: true,
    size: 0.4,
  },
  {
    accessorKey: "businessUnit",
    header: "Business Unit",
    enableColumnFilter: true,
    size: 40,
    Cell: ({ cell }: any) => {
      const value = cell.getValue() as string;
      if (!value) {
        return <Box sx={{ paddingLeft: "15px" }}>{EMPTY_CELL_VALUE}</Box>;
      }
      return (
        <Tooltip title={value}>
          <Box
            sx={{
              maxWidth: '160px',
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            {value}
          </Box>
        </Tooltip>
      );
    },
  },
  {
    accessorKey: "city",
    header: "City",
    enableColumnFilter: true,
    size: 0.4,
  },
  {
    accessorKey: "country",
    header: "Country",
    Cell: ({ cell }) => {
      const value = cell.getValue() as string;
      if (isNullOrUndefined(value)) {
        return EMPTY_CELL_VALUE;
      }
      return (
        <div>{value.replaceAll("_", " ")}</div>
      )
    },
    enableColumnFilter: true,
    size: 0.4,
  },
  {
    accessorKey: "countryRegion",
    header: "Region",
    enableColumnFilter: true,
    size: 0.4,
    Cell: ({ row }) => (
      <div style={{ display: "flex", padding: "none" }}>
        <p>{row.original.countryRegion}</p>
      </div>
    ),
  },
  {
    accessorKey: "users",
    header: "Data Provider",
    enableColumnFilter: true,
    size: 0.4,
    Cell: ({ row }) => {
      const { users } = row.original;

      if (!users || users.length === 0 || isNullOrUndefined(users[0]?.firstName)) {
        return EMPTY_CELL_VALUE;
      }

      const firstName = capitalizeString(users[0].firstName);
      const extraCount = users.length - 1;

      return (
        <div style={{ display: "flex", padding: "none" }}>
          <p>
            {firstName}
            {users.length > 1 && ` +${extraCount}`}
          </p>
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    enableColumnFilter: true,
    size: 0.4,
    Cell: ({ row }) => (
      <div style={{ display: "flex", padding: "none" }}>
        <p>{row.original.status}</p>
      </div>
    ),
  },
];
